import { useState } from 'react';
import { analysesApi, summaryApi } from '@/shared/api/endpoints';
import type { Analysis, AnalysisMetrics, AnalysisPeriod, DaySummary } from '@/shared/api/types';
import { formatDayShort, shiftIsoDate, todayIso } from '@/shared/format/dates';
import { formatGrams, formatKcal, formatPercentage } from '@/shared/format/nutrition';
import { describeError, useAsync } from '@/shared/hooks/useAsync';
import { Banner, Button, Card, Eyebrow } from '@/shared/ui';
import { EsqueletoDetalle, useEsqueleto } from '@/shared/ui/Skeleton';
import { DailyBars } from './DailyBars';

const PERIODS = [
  { value: 'Week' as const, label: 'Semanal' },
  { value: 'Month' as const, label: 'Mensual' },
];

/** El gráfico por día es de la semana: treinta barras en 360 px no se leen. */
const CHART_DAYS = 7;

function parseMetrics(raw: string): AnalysisMetrics | null {
  try {
    return JSON.parse(raw) as AnalysisMetrics;
  } catch {
    return null;
  }
}

export function AnalysesPage() {
  const [period, setPeriod] = useState<AnalysisPeriod>('Week');
  const analysis = useAsync<Analysis>(() => analysesApi.get(period), [period]);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const days = useAsync<DaySummary[]>(
    () =>
      Promise.all(
        Array.from({ length: CHART_DAYS }, (_, index) =>
          summaryApi.day(shiftIsoDate(todayIso(), index - (CHART_DAYS - 1))),
        ),
      ),
    [],
  );

  const regenerate = async () => {
    setRegenerating(true);
    setError(null);
    try {
      await analysesApi.regenerate(period);
      analysis.reload();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setRegenerating(false);
    }
  };

  const metrics = analysis.data === null ? null : parseMetrics(analysis.data.metrics);
  const coveredDays = metrics === null ? 0 : metrics.days_within_goal + metrics.days_outside_goal;

  const esqueleto = useEsqueleto(analysis.loading);

  return (
    <>
      <header>
        {analysis.data !== null && (
          <Eyebrow>
            {formatDayShort(analysis.data.fromInclusive)} al{' '}
            {formatDayShort(analysis.data.toInclusive)}
          </Eyebrow>
        )}
        <h1>Análisis</h1>
      </header>

      <div className="segmented" role="group" aria-label="Período">
        {PERIODS.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={option.value === period}
            onClick={() => setPeriod(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {error !== null && <Banner tone="danger">{error}</Banner>}
      {esqueleto && analysis.data === null && (
        <EsqueletoDetalle etiqueta="Cargando el análisis" />
      )}

      {analysis.error !== null && !analysis.loading && <InsufficientData reason={analysis.error} />}

      {/*
        RF-42 y RN-07 · las métricas se calculan en la base y llegan ya sumadas. Ninguna de estas
        cifras la produjo un modelo de lenguaje, y por eso van arriba: son el dato.
      */}
      {metrics !== null && (
        <div className="metric-grid">
          {/*
            Con cero días cubiertos, todas estas cifras son divisiones sobre nada. Un «0 %» de
            adherencia se lee como mal desempeño —rozando el juicio que RN-09 prohíbe— cuando lo
            único que pasó es que no hubo registros. La raya dice «no hay dato», que es la verdad.
          */}
          <Metric
            label="Promedio diario"
            value={coveredDays === 0 ? '—' : `${formatKcal(metrics.average_kcal)} kcal`}
          />
          <Metric
            label="Adherencia"
            value={coveredDays === 0 ? '—' : formatPercentage(metrics.adherence_percentage)}
          />
          <Metric
            label="Días dentro del objetivo"
            value={coveredDays === 0 ? '—' : `${metrics.days_within_goal} de ${coveredDays}`}
          />
          <Metric
            label="Desvío promedio"
            value={coveredDays === 0 ? '—' : `${formatKcal(metrics.average_deviation_kcal)} kcal`}
          />
        </div>
      )}

      {period === 'Week' && days.data !== null && (
        <Card>
          <Eyebrow>Consumo por día</Eyebrow>
          <DailyBars days={days.data} goalKcal={metrics?.goal_kcal ?? null} />
        </Card>
      )}

      {metrics !== null && (
        <Card size="tight">
          <div className="row-between micro">
            <span className="muted">Proteína</span>
            <span className="numeric">{formatGrams(metrics.average_protein_g)} g por día</span>
          </div>
          <div className="row-between micro">
            <span className="muted">Carbohidratos</span>
            <span className="numeric">
              {formatGrams(metrics.average_carbohydrates_g)} g por día
            </span>
          </div>
          <div className="row-between micro">
            <span className="muted">Grasa</span>
            <span className="numeric">{formatGrams(metrics.average_fat_g)} g por día</span>
          </div>
          {metrics.weight_change_kg !== null && (
            <div className="row-between micro">
              <span className="muted">Cambio de peso</span>
              <span className="numeric">
                {metrics.weight_change_kg > 0 ? '+' : ''}
                {formatGrams(metrics.weight_change_kg)} kg
              </span>
            </div>
          )}
        </Card>
      )}

      {analysis.data !== null && analysis.data.incomplete && (
        <Banner tone="info">
          El período tiene días sin registros. Las métricas se calculan sólo sobre los días que sí
          tienen.
        </Banner>
      )}

      {/*
        La narrativa va abajo y visualmente separada del resto. Es una interpretación escrita sobre
        métricas ya calculadas, no una fuente de datos: mezclarla con los números la haría parecer
        tan verificable como ellos.
      */}
      {analysis.data !== null &&
        (analysis.data.narrative === null ? (
          <InsufficientNarrative />
        ) : (
          <div className="meal-card">
            <div className="narrative-head">
              <Eyebrow>Interpretación generada</Eyebrow>
              <Button
                variant="plain"
                disabled={regenerating}
                onClick={() => void regenerate()}
                style={{ minHeight: 32 }}
              >
                {regenerating ? 'Regenerando…' : 'Regenerar'}
              </Button>
            </div>
            <div style={{ padding: 'var(--space-4)' }} className="stack">
              <p className="narrative">{analysis.data.narrative}</p>
              <p className="nano muted">
                Es un texto generado sobre métricas ya calculadas. Motor{' '}
                {analysis.data.engineVersion}.
              </p>
            </div>
          </div>
        ))}

      {/*
        RN-09 pide la aclaración en TODA salida, y acá vivía sólo dentro de la rama con narrativa:
        cuando el período no llegaba a la cobertura mínima —que es el caso más frecuente— la
        pantalla mostraba métricas sobre la alimentación de una persona sin ninguna advertencia.
        El número solo también es una lectura sobre su salud.
      */}
      {analysis.data !== null && (
        <p className="nano muted" style={{ textAlign: 'center' }}>
          Esto complementa, no reemplaza, la consulta con un profesional de la salud.
        </p>
      )}
    </>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Card size="tight">
      <span className="metric-value">{value}</span>
      <span className="nano muted">{label}</span>
    </Card>
  );
}

/**
 * RN-12 · sin datos suficientes las métricas siguen siendo reales, pero la narrativa no se
 * escribe. Los cuadraditos muestran cuánto falta sin pedir que nadie cuente los días.
 */
function InsufficientNarrative() {
  return (
    <div className="card-dashed">
      <div className="row">
        {[true, true, false, false].map((filled, index) => (
          <span
            key={index}
            style={{
              width: 12,
              height: 12,
              borderRadius: 3,
              background: filled ? 'var(--conf)' : 'transparent',
              border: filled ? 'none' : '1px dashed var(--rule)',
            }}
          />
        ))}
      </div>
      <strong className="small">Todavía no hay con qué escribir la lectura</strong>
      <span className="micro muted">
        Las métricas de arriba son reales y salen de tus registros. El texto se escribe cuando el
        período tenga suficientes días cargados.
      </span>
    </div>
  );
}

function InsufficientData({ reason }: { reason: string }) {
  return (
    <div className="card-dashed">
      <strong className="small">Todavía no hay análisis de este período</strong>
      <span className="micro muted">{reason}</span>
    </div>
  );
}
